/**
 * Iter-235j — Standalone PREVIEW route for the Shift Metrics Wizard.
 *
 * Lets admins (or the founder) view the actual wizard UI **without**
 * being routed through clock-out. The page lists the admin's own
 * completed bookings; clicking one opens the wizard against that
 * booking_id so the AI extraction + form behavior can be exercised
 * end-to-end. Once the workflow is approved we'll wire it into the
 * real Clock-Out flow in Iter-235k.
 */
import React, { useEffect, useState, lazy, Suspense } from 'react';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Loader2, FlaskConical, ChevronRight } from 'lucide-react';

// Iter-235j — Lazy-load the wizard so the visual-edits babel metadata
// plugin doesn't try to cross-file-trace its props during build. (The
// plugin has a known crash on ImportDeclaration nodes inside newly
// created files — we hit the same issue with VoiceRecapRecorder in
// Iter-235e and React.lazy is the established workaround.)
const ShiftMetricsWizard = lazy(() => import('../../components/ShiftMetricsWizard'));

const AdminShiftMetricsPreview = () => {
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Show the most recent completed bookings — any of them can
        // serve as a wizard preview target.
        const r = await api.get('/admin/bookings?status=completed&limit=10').catch(() => ({ data: [] }));
        const items = Array.isArray(r.data) ? r.data : (r.data?.rows || r.data?.bookings || []);
        if (!cancelled) setBookings(items);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (selected) {
    return (
      <div className="p-4 max-w-3xl mx-auto" data-testid="admin-shift-metrics-preview-wizard">
        <Button variant="ghost" onClick={() => setSelected(null)} className="mb-3 text-xs" data-testid="preview-close-btn">
          ← Back to booking list
        </Button>
        <Suspense fallback={<div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-cyan-500" /></div>}>
          <ShiftMetricsWizard
            bookingId={selected}
            onClose={() => setSelected(null)}
            onSubmitted={() => setSelected(null)}
            autoExtractFromVoice={true}
          />
        </Suspense>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-3xl mx-auto" data-testid="admin-shift-metrics-preview">
      <div className="flex items-center gap-2 mb-3">
        <FlaskConical className="w-5 h-5 text-amber-500" />
        <h1 className="text-xl font-bold">Shift Metrics Wizard · UI Preview</h1>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Pick a completed booking to preview the new Shift Recap workflow.
        AI extraction will fire if the talent submitted a voice recap.
      </p>
      {loading ? (
        <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-cyan-500" /></div>
      ) : bookings.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-gray-500">
          No completed bookings in the demo dataset. Paste a booking_id manually:
          <div className="mt-3">
            <input
              placeholder="booking_id (uuid)"
              className="border border-gray-200 rounded px-3 py-2 text-sm w-full"
              data-testid="preview-manual-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.target.value.trim()) {
                  setSelected(e.target.value.trim());
                }
              }}
            />
            <p className="text-[10px] text-gray-400 mt-1">Press Enter to open.</p>
          </div>
        </CardContent></Card>
      ) : (
        <Card><CardContent className="p-0">
          {bookings.map((b) => (
            <button
              key={b.id || b.booking_id}
              type="button"
              onClick={() => setSelected(b.id || b.booking_id)}
              className="w-full flex items-center justify-between px-4 py-3 border-b last:border-b-0 hover:bg-gray-50 text-left"
              data-testid={`preview-booking-${b.id || b.booking_id}`}
            >
              <div>
                <p className="text-sm font-medium text-gray-900">{b.shift_title || b.title || 'Untitled shift'}</p>
                <p className="text-[10px] text-gray-500">
                  Talent: {b.talent_email || b.talent_name || b.talent_id?.slice(0, 8)} ·
                  Booking: <code className="text-[10px]">{(b.id || b.booking_id)?.slice(0, 12)}…</code>
                </p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </button>
          ))}
        </CardContent></Card>
      )}
    </div>
  );
};

export default AdminShiftMetricsPreview;
